import { useState } from "react";
import {
  ExtendSubscriptionRequest,
  OrgEntitlements,
  PAYMENT_METHOD_OPTIONS,
  Plan,
  PaymentMethodType,
} from "../models/SubscriptionModels";
import { formatDate } from "../utils/DateTime";
import "./SubscriptionForms.css";

interface ExtendSubscriptionFormProps {
  organizationId: string;
  entitlements: OrgEntitlements | null;
  plan: Plan | null;
  submitting: boolean;
  onCancel?: () => void;
  onSubmit: (request: ExtendSubscriptionRequest) => void;
}

const ExtendSubscriptionForm = ({
  organizationId,
  entitlements,
  plan,
  submitting,
  onCancel,
  onSubmit,
}: ExtendSubscriptionFormProps) => {
  const [periodCount, setPeriodCount] = useState("1");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");

  const billingPeriodDays = plan?.billingPeriodDays ?? 30;

  // Client-side preview: new end = current endsAt (if in future) + periods.
  const previewEnd = ((): Date | null => {
    const periods = Math.max(1, Number(periodCount) || 1);
    const base = entitlements?.endsAt
      ? new Date(entitlements.endsAt)
      : new Date();
    if (Number.isNaN(base.getTime())) return null;
    const end = new Date(base);
    end.setDate(end.getDate() + periods * billingPeriodDays);
    return end;
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const periods = Math.max(1, Number(periodCount) || 1);
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return;

    onSubmit({
      organizationId,
      periodCount: periods,
      amount: parsedAmount,
      paymentMethod: Number(paymentMethod) as PaymentMethodType,
      paymentReference: paymentReference.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <form className="sub-form" onSubmit={handleSubmit}>
      <div className="sub-form__summary">
        <span>
          Current plan: <strong>{entitlements?.planName ?? plan?.name ?? "—"}</strong>
        </span>
        <span>
          Current end:{" "}
          <strong>{formatDate(entitlements?.endsAt ?? null)}</strong>
        </span>
        {previewEnd && (
          <span>
            New end:{" "}
            <strong className="sub-form__preview">
              {formatDate(previewEnd.toISOString())}
            </strong>
          </span>
        )}
      </div>

      <div className="sub-form__grid">
        <div className="sub-form__field">
          <label htmlFor="ext-periods">Period count *</label>
          <input
            id="ext-periods"
            type="number"
            min={1}
            value={periodCount}
            disabled={submitting}
            onChange={(e) => setPeriodCount(e.target.value)}
          />
          <span className="sub-form__hint">
            Number of billing periods (e.g. 1 = {billingPeriodDays} days if plan is monthly)
          </span>
        </div>

        <div className="sub-form__field">
          <label htmlFor="ext-amount">Amount ({plan?.currency ?? "BDT"}) *</label>
          <input
            id="ext-amount"
            type="number"
            min={0}
            step="0.01"
            value={amount}
            placeholder="0"
            disabled={submitting}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div className="sub-form__field">
          <label htmlFor="ext-method">Payment method *</label>
          <select
            id="ext-method"
            value={paymentMethod}
            disabled={submitting}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <option value="">Select payment method…</option>
            {PAYMENT_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sub-form__field">
          <label htmlFor="ext-reference">Payment reference</label>
          <input
            id="ext-reference"
            type="text"
            value={paymentReference}
            placeholder="e.g. BANK-REF"
            disabled={submitting}
            onChange={(e) => setPaymentReference(e.target.value)}
          />
        </div>

        <div className="sub-form__field sub-form__field--full">
          <label htmlFor="ext-notes">Notes</label>
          <textarea
            id="ext-notes"
            rows={2}
            value={notes}
            disabled={submitting}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="sub-form__actions">
        {onCancel && (
          <button
            type="button"
            className="sub-form__btn sub-form__btn--secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="sub-form__btn sub-form__btn--primary"
          disabled={
            submitting ||
            !paymentMethod ||
            !amount ||
            Number(amount) <= 0
          }
        >
          {submitting ? "Extending…" : "Extend subscription"}
        </button>
      </div>
    </form>
  );
};

export default ExtendSubscriptionForm;